import { observer } from 'mobx-react';
import { useState } from 'react';
import { CalendarIcon, CloseIcon } from '../../../VaultFilters/icons';
import {
    AddButton,
    RemoveTagButton,
    TagCategory,
    TagCategoryTitle,
    TagPill,
    TagsList,
    TagText
} from '../shared/TagCategory.styles';
import {
    PeriodButton,
    PeriodButtonContainer,
    PeriodEditorContainer,
    QuarterOption,
    QuarterSelector,
    YearArrow,
    YearDisplay,
    YearSelector
} from './PeriodTagCategory.styles';
import { useStore } from '../../../../../app/data/root.store';
import { ActionLockedIconWithTooltip } from '../../../../../../common/components/ActionLocked/ActionLockedIconWithTooltip';

export const PeriodTagCategory = observer(({ periodTags, onUpdate }) => {
    const [showPeriodEditor, setShowPeriodEditor] = useState(false);
    const [selectedYear, setSelectedYear] = useState(2025);
    const [selectedPeriods, setSelectedPeriods] = useState(new Set());
    const { userStore } = useStore();

    const handleAddTag = () => {
        if (userStore.isActionBlockedByRole('editVaultFileMetadata')) {
            userStore.setShowLimitedSharedAccess(true);
            return;
        }
        const existingPeriods = new Set(periodTags);
        setSelectedPeriods(existingPeriods);
        setShowPeriodEditor(true);
    };

    const handlePillClick = (tag) => {
        if (userStore.isActionBlockedByRole('editVaultFileMetadata')) {
            userStore.setShowLimitedSharedAccess(true);
            return;
        }
        const yearMatch = tag.match(/\d{4}/);
        if (yearMatch) {
            setSelectedYear(parseInt(yearMatch[0]));
        }
        
        const existingPeriods = new Set(periodTags);
        setSelectedPeriods(existingPeriods);
        setShowPeriodEditor(true);
    };

    const handleRemoveTag = (tagToRemove) => {
        if (userStore.isActionBlockedByRole('editVaultFileMetadata')) {
            userStore.setShowLimitedSharedAccess(true);
            return;
        }
        const updatedPeriods = periodTags.filter(tag => tag !== tagToRemove);
        onUpdate(updatedPeriods);
    };

    const handlePeriodToggle = (period) => {
        setSelectedPeriods(prev => {
            const newSelection = new Set();
            
            if (period === selectedYear.toString()) {
                if (!prev.has(period)) {
                    newSelection.add(period);
                }
            } else {
                if (!prev.has(period)) {
                    newSelection.add(period);
                }
            }
            
            return newSelection;
        });
    };

    const handlePeriodSave = () => {
        onUpdate(Array.from(selectedPeriods));
        setShowPeriodEditor(false);
    };

    const handlePeriodCancel = () => {
        setShowPeriodEditor(false);
    };

    const isYearSelected = () => {
        return selectedPeriods.has(selectedYear.toString());
    };

    const isQuarterSelected = (quarter) => {
        return selectedPeriods.has(`${quarter}-${selectedYear}`);
    };

    return (
        <TagCategory>
            <TagCategoryTitle>Period {userStore.isActionBlockedByRole('editVaultFileMetadata') && <ActionLockedIconWithTooltip iconColor={'#ACB1BB'} isRoleBlocked={true} marginLeft={'0px'} />}
            </TagCategoryTitle>
            {showPeriodEditor ? (
                <PeriodEditorContainer>
                    <YearSelector>
                        <YearArrow isLeft onClick={() => setSelectedYear(prev => prev - 1)}>‹</YearArrow>
                        <YearDisplay>{selectedYear}</YearDisplay>
                        <YearArrow isRight onClick={() => setSelectedYear(prev => prev + 1)}>›</YearArrow>
                    </YearSelector>
                    <QuarterSelector>
                        <QuarterOption 
                            isSelected={isYearSelected()}
                            onClick={() => handlePeriodToggle(selectedYear.toString())}
                        >
                            All
                        </QuarterOption>
                        {['Q1', 'Q2', 'Q3', 'Q4'].map(quarter => (
                            <QuarterOption 
                                key={quarter}
                                isSelected={isQuarterSelected(quarter)}
                                onClick={() => handlePeriodToggle(`${quarter}-${selectedYear}`)}
                            >
                                {quarter}
                            </QuarterOption>
                        ))}
                    </QuarterSelector>
                    <PeriodButtonContainer>
                        <PeriodButton variant="secondary" onClick={handlePeriodCancel}>
                            Cancel
                        </PeriodButton>
                        <PeriodButton variant="primary" onClick={handlePeriodSave}>
                            Save
                        </PeriodButton>
                    </PeriodButtonContainer>
                </PeriodEditorContainer>
            ) : (
                <>
                    {periodTags.length > 0 && (
                        <TagsList>
                            {periodTags.map((tag, index) => (
                                <TagPill key={index} onClick={() => handlePillClick(tag)}>
                                    <CalendarIcon />
                                    <TagText>{tag}</TagText>
                                    <RemoveTagButton 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveTag(tag);
                                        }}
                                    >
                                        <CloseIcon />
                                    </RemoveTagButton>
                                </TagPill>
                            ))}
                        </TagsList>
                    )}
                    {periodTags.length === 0 ? <AddButton onClick={handleAddTag}>+ Add</AddButton> : null}
                </>
            )}
        </TagCategory>
    );
}); 